import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { usersApi } from '../../services/api';
import './AdminLossOfPayReport.css';

const buildEmployeeName = (user) => `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unnamed employee';
const getTodayDateOnly = () => new Date().toISOString().slice(0, 10);
const DEFAULT_START_DATE = getTodayDateOnly();
const DEFAULT_END_DATE = getTodayDateOnly();
const CSV_COLUMNS = [
  { key: 'name', label: 'Name of Employee' },
  { key: 'joiningDate', label: 'Joining Date' },
  { key: 'employmentType', label: 'Employment Type' },
  { key: 'elRollover', label: 'EL Rollover' },
  { key: 'elAllocated', label: 'EL Allocated' },
  { key: 'elTakenYtd', label: 'EL YTD' },
  { key: 'elTakenMtd', label: 'EL MTD' },
  { key: 'elTakenSelected', label: 'EL Selected Period' },
  { key: 'elBalance', label: 'EL Balance' },
  { key: 'elPlanned', label: 'EL Planned' },
  { key: 'slTakenYtd', label: 'SL YTD' },
  { key: 'slTakenMtd', label: 'SL MTD' },
  { key: 'slTakenSelected', label: 'SL Selected Period' },
  { key: 'compTakenYtd', label: 'Comp YTD' },
  { key: 'compTakenMtd', label: 'Comp MTD' },
  { key: 'compTakenSelected', label: 'Comp Selected Period' },
  { key: 'lossOfPayDays', label: 'Loss of Pay (Selected Period)' },
];

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return '';
  const asString = String(value);
  if (/[",\n]/.test(asString)) {
    return `"${asString.replace(/"/g, '""')}"`;
  }
  return asString;
};

export default function AdminLossOfPayReportPage() {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState('all');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE);
  const [reportRows, setReportRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const employeeDropdownRef = useRef(null);
  const [detailsRow, setDetailsRow] = useState(null);
  const [detailsTab, setDetailsTab] = useState('leaves');
  const [detailsData, setDetailsData] = useState({ leaves: [], attendance: [] });
  const [detailsLoading, setDetailsLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    const res = await usersApi.list({ limit: 1000 });
    const rows = Array.isArray(res.data.data) ? res.data.data : (res.data.data?.rows || []);
    const sorted = [...rows].sort((a, b) => buildEmployeeName(a).localeCompare(buildEmployeeName(b)));
    setUsers(sorted);
    return sorted;
  }, []);

  const loadReport = useCallback(async (overrides = {}) => {
    const effectiveStartDate = overrides.startDate ?? startDate;
    const effectiveEndDate = overrides.endDate ?? endDate;
    const effectiveUserId = overrides.userId ?? selectedUserId;

    if (!effectiveStartDate || !effectiveEndDate || effectiveStartDate > effectiveEndDate) {
      toast.error('Select a valid report period');
      return;
    }

    setLoadingReport(true);
    try {
      const params = {
        startDate: effectiveStartDate,
        endDate: effectiveEndDate,
      };
      if (effectiveUserId !== 'all') {
        params.userId = effectiveUserId;
      }

      const reportRes = await usersApi.lopReport(params);
      setReportRows(reportRes.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoadingReport(false);
    }
  }, [endDate, selectedUserId, startDate]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      setLoading(true);
      try {
        await loadUsers();
        if (!mounted) return;
        await loadReport();
      } catch (err) {
        toast.error('Failed to load report data');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => { mounted = false; };
  }, []);

  const visibleReportRows = useMemo(() => {
    if (employeeTypeFilter === 'all') return reportRows;
    return reportRows.filter((row) => row.employmentType === employeeTypeFilter);
  }, [reportRows, employeeTypeFilter]);

  const summary = useMemo(() => {
    const employees = visibleReportRows.length;
    const totalEarnedUsed = visibleReportRows.reduce((sum, row) => sum + row.elTakenSelected, 0);
    const totalSickUsed = visibleReportRows.reduce((sum, row) => sum + row.slTakenSelected, 0);
    const totalCompensationUsed = visibleReportRows.reduce((sum, row) => sum + row.compTakenSelected, 0);
    const totalLossOfPayDays = visibleReportRows.reduce((sum, row) => sum + row.lossOfPayDays, 0);
    return { employees, totalEarnedUsed, totalSickUsed, totalCompensationUsed, totalLossOfPayDays };
  }, [visibleReportRows]);

  const selectedEmployeeLabel = useMemo(() => {
    if (selectedUserId === 'all') return 'All';
    const selected = users.find((user) => user.id === selectedUserId);
    return selected ? buildEmployeeName(selected) : 'Select employee';
  }, [selectedUserId, users]);

  const filteredUsers = useMemo(() => {
    const search = employeeSearch.trim().toLowerCase();
    const matchedUsers = !search
      ? users
      : users.filter((user) => buildEmployeeName(user).toLowerCase().includes(search));

    if (selectedUserId === 'all' || matchedUsers.some((user) => user.id === selectedUserId)) {
      return matchedUsers;
    }

    const selectedUser = users.find((user) => user.id === selectedUserId);
    return selectedUser ? [selectedUser, ...matchedUsers] : matchedUsers;
  }, [employeeSearch, selectedUserId, users]);

  const resetFilters = () => {
    setSelectedUserId('all');
    setEmployeeTypeFilter('all');
    setEmployeeSearch('');
    setIsEmployeeDropdownOpen(false);
    setStartDate(DEFAULT_START_DATE);
    setEndDate(DEFAULT_END_DATE);
    // State setters above don't apply until the next render, so pass the reset
    // values straight through instead of relying on stale closed-over state.
    loadReport({ startDate: DEFAULT_START_DATE, endDate: DEFAULT_END_DATE, userId: 'all' });
  };

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!employeeDropdownRef.current?.contains(event.target)) {
        setIsEmployeeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const handleEmployeeSelect = (userId) => {
    setSelectedUserId(userId);
    setIsEmployeeDropdownOpen(false);
    setEmployeeSearch('');
  };

  const openDetailsModal = async (row) => {
    setDetailsRow(row);
    setDetailsTab('leaves');
    setDetailsData({ leaves: [], attendance: [] });
    setDetailsLoading(true);
    try {
      const res = await usersApi.lopReportDetails(row.id, { startDate, endDate });
      setDetailsData(res.data.data || { leaves: [], attendance: [] });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load employee details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setDetailsRow(null);
    setDetailsData({ leaves: [], attendance: [] });
  };

  const handleDetailsExportCsv = () => {
    if (!detailsRow) return;
    if (detailsData.leaves.length === 0 && detailsData.attendance.length === 0) {
      toast.error('No details available to export');
      return;
    }

    const leaveHeader = ['Leave Type', 'Start Date', 'End Date', 'Days', 'Status', 'Reason'].map(escapeCsvValue).join(',');
    const leaveRows = detailsData.leaves.map((l) =>
      [l.leave_type, l.start_date, l.end_date, l.days_requested, l.status, l.reason].map(escapeCsvValue).join(',')
    );
    const attendanceHeader = ['Date', 'Status', 'Check-in', 'Check-out', 'Work Description'].map(escapeCsvValue).join(',');
    const attendanceRows = detailsData.attendance.map((a) =>
      [a.date, a.status, a.check_in, a.check_out, a.work_description].map(escapeCsvValue).join(',')
    );

    const csvContent = [
      `Employee,${escapeCsvValue(detailsRow.name)}`,
      `Period,${escapeCsvValue(`${startDate} to ${endDate}`)}`,
      '',
      'LEAVES',
      leaveHeader,
      ...leaveRows,
      '',
      'ATTENDANCE',
      attendanceHeader,
      ...attendanceRows,
    ].join('\n');

    const fileName = `${detailsRow.name.replace(/\s+/g, '-').toLowerCase()}-details-${startDate}-to-${endDate}.csv`;
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDetailsDownloadPdf = () => {
    // Browser print-to-PDF — the print stylesheet below hides everything
    // except the details modal, so the user just picks "Save as PDF".
    window.print();
  };

  const handleExportCsv = () => {
    if (!visibleReportRows.length) {
      toast.error('No report rows available to export');
      return;
    }

    const header = CSV_COLUMNS.map((column) => escapeCsvValue(column.label)).join(',');
    const rows = visibleReportRows.map((row) => {
      const values = CSV_COLUMNS.map((column) => {
        const value = row[column.key];
        if (typeof value === 'number') {
          return escapeCsvValue(value.toFixed(2));
        }
        return escapeCsvValue(value);
      });
      return values.join(',');
    });

    const csvContent = [header, ...rows].join('\n');
    const fileName = `employee-report-${startDate}-to-${endDate}.csv`;
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="lop-report-page">
      <div className="">
        <div>
          {/* <div className="lop-report-brand">Aays</div> */}
          <h1>Employee Report</h1>
          <p>Accounts-ready leave report with earned leave and sick leave for the selected period.</p>
        </div>
      </div>

      <div className="card lop-toolbar animate-in">
        <div className="lop-toolbar-top">
          <div>
            <h2>Report Filters</h2>
            <p>Narrow the report by employee and period, then regenerate the latest view.</p>
          </div>
          <div className="lop-toolbar-chips">
            <button className="btn btn-secondary btn-sm" onClick={resetFilters} disabled={loadingReport}>
              Reset
            </button>
          </div>
        </div>

        <div className="lop-toolbar-grid">
          <div className="lop-toolbar-field">
            <label>Select an Employee</label>
            <div className="lop-employee-dropdown" ref={employeeDropdownRef}>
              <button
                type="button"
                className="lop-employee-trigger"
                onClick={() => setIsEmployeeDropdownOpen((open) => !open)}
                aria-expanded={isEmployeeDropdownOpen}
              >
                <span>{selectedEmployeeLabel}</span>
                <span className={`lop-trigger-caret ${isEmployeeDropdownOpen ? 'lop-trigger-caret--open' : ''}`}>▾</span>
              </button>

              {isEmployeeDropdownOpen && (
                <div className="lop-employee-menu">
                  <input
                    className="form-input lop-employee-search"
                    type="text"
                    placeholder="Search employee name"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    autoFocus
                  />

                  <div className="lop-employee-options">
                    <button
                      type="button"
                      className={`lop-employee-option ${selectedUserId === 'all' ? 'lop-employee-option--active' : ''}`}
                      onClick={() => handleEmployeeSelect('all')}
                    >
                      All
                    </button>
                    {filteredUsers.map((user) => (
                      <button
                        type="button"
                        key={user.id}
                        className={`lop-employee-option ${selectedUserId === user.id ? 'lop-employee-option--active' : ''}`}
                        onClick={() => handleEmployeeSelect(user.id)}
                      >
                        {buildEmployeeName(user)}
                      </button>
                    ))}
                    {filteredUsers.length === 0 && <div className="lop-employee-empty">No employee found</div>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lop-toolbar-field lop-toolbar-field--period">
            <label>Select Period</label>
            <div className="lop-date-range">
              <input className="form-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <span>to</span>
              <input className="form-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          <div className="lop-toolbar-field">
            <label>Employment Type</label>
            <select className="form-select" value={employeeTypeFilter} onChange={(e) => setEmployeeTypeFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="Full Time">Full Time</option>
              <option value="Contractor">Contractor</option>
            </select>
          </div>

          <div className="lop-toolbar-actions">
            <button className="btn btn-secondary lop-export-button" onClick={handleExportCsv} disabled={loading || loadingReport || visibleReportRows.length === 0}>
              Export CSV
            </button>
            <button className="btn btn-primary lop-refresh-button" onClick={() => loadReport()} disabled={loadingReport}>
              {loadingReport ? 'Generating…' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      <div className="lop-summary-grid">
        <div className="lop-summary-card">
          <span>Employees in Report</span>
          {loading || loadingReport ? <div className="skeleton" style={{ height: 22, width: 60, borderRadius: 6 }} /> : <strong>{summary.employees}</strong>}
        </div>
        <div className="lop-summary-card">
          <span>Selected Period Earned Leave</span>
          {loading || loadingReport ? <div className="skeleton" style={{ height: 22, width: 60, borderRadius: 6 }} /> : <strong>{summary.totalEarnedUsed.toFixed(2)}</strong>}
        </div>
        <div className="lop-summary-card">
          <span>Selected Period Sick Leave</span>
          {loading || loadingReport ? <div className="skeleton" style={{ height: 22, width: 60, borderRadius: 6 }} /> : <strong>{summary.totalSickUsed.toFixed(2)}</strong>}
        </div>
        <div className="lop-summary-card">
          <span>Selected Period Compensation Leave</span>
          {loading || loadingReport ? <div className="skeleton" style={{ height: 22, width: 60, borderRadius: 6 }} /> : <strong>{summary.totalCompensationUsed.toFixed(2)}</strong>}
        </div>
        <div className="lop-summary-card" style={!loading && !loadingReport && summary.totalLossOfPayDays > 0 ? { background: '#FEE2E2', color: '#991B1B' } : undefined}>
          <span>Loss of Pay Days (Selected Period)</span>
          {loading || loadingReport ? <div className="skeleton" style={{ height: 22, width: 60, borderRadius: 6 }} /> : <strong>{summary.totalLossOfPayDays.toFixed(2)}</strong>}
        </div>
      </div>

      <div className="lop-table-card animate-in stagger-1" style={{ padding: 0 }}>
        <div className="">
          <div>
            <h2>Detailed Leave Analysis</h2>
          </div>
          <span style={{fontSize:"14px",color:"gray"}} className="">Period {startDate} to {endDate} · {summary.employees} employees</span>
        </div>
        <div style={{fontSize:"12px",color:"gray"}}>Tip: Scroll horizontally to view all leave metrics.</div>

        <div className="table-wrapper lop-table-wrapper">
          <table>
            <thead>
              <tr className="lop-group-row">
                <th className="lop-col-employee" rowSpan={2}>Name of Employee</th>
                <th className="lop-group-header lop-col--el" colSpan={7}>Earned Leave (EL)</th>
                <th className="lop-group-header lop-col--sl" colSpan={3}>Sick Leave (SL)</th>
                <th className="lop-group-header lop-col--comp" colSpan={3}>Compensation Leave (Comp)</th>
                <th rowSpan={2}>Loss of Pay (Period)</th>
                <th rowSpan={2}>Details</th>
              </tr>
              <tr className="lop-subhead-row">
                <th className="lop-col--el lop-col--el-first">Rollover</th>
                <th className="lop-col--el">Allocated</th>
                <th className="lop-col--el">YTD</th>
                <th className="lop-col--el">MTD</th>
                <th className="lop-col--el">Selected Period</th>
                <th className="lop-col--el">Balance</th>
                <th className="lop-col--el">Planned</th>
                <th className="lop-col--sl lop-col--sl-first">YTD</th>
                <th className="lop-col--sl">MTD</th>
                <th className="lop-col--sl">Selected Period</th>
                <th className="lop-col--comp lop-col--comp-first">YTD</th>
                <th className="lop-col--comp">MTD</th>
                <th className="lop-col--comp">Selected Period</th>
              </tr>
            </thead>
            <tbody>
              {loading || loadingReport ? (
                [...Array(6)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={16}><div className="skeleton" style={{ height: 44, borderRadius: 10 }} /></td>
                  </tr>
                ))
              ) : visibleReportRows.length === 0 ? (
                <tr>
                  <td colSpan={16}>
                    <div className="empty-state">
                      <div className="empty-state-icon">▣</div>
                      <div className="empty-state-title">No report rows found</div>
                    </div>
                  </td>
                </tr>
              ) : (
                visibleReportRows.map((row) => (
                  <tr key={row.id}>
                    <td className="lop-col-employee">
                      <div className="lop-employee-cell">
                        <div className="avatar avatar-sm">{row.name.split(' ').map((part) => part[0]).join('').slice(0, 2)}</div>
                        <div>
                          <div className="lop-employee-name">{row.name}</div>
                          <div className="lop-employee-subtitle">
                            <span className="lop-employment-badge">{row.employmentType}</span> · {row.joiningDate}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="lop-cell-number lop-col--el">{row.elRollover.toFixed(2)}</td>
                    <td className="lop-cell-number lop-col--el">{row.elAllocated.toFixed(2)}</td>
                    <td className={`lop-cell-number lop-col--el ${row.elTakenYtd > row.elAllocated + row.elRollover ? 'lop-cell-danger' : ''}`} title={row.elTakenYtd > row.elAllocated + row.elRollover ? 'Earned leave taken this year exceeds policy entitlement' : undefined}>{row.elTakenYtd.toFixed(2)}</td>
                    <td className="lop-cell-number lop-col--el">{row.elTakenMtd.toFixed(2)}</td>
                    <td className={`lop-cell-number lop-col--el ${row.elExcess > 0 ? 'lop-cell-danger' : ''}`} title={row.elExcess > 0 ? 'Earned leave taken exceeds available policy balance' : undefined}>{row.elTakenSelected.toFixed(2)}</td>
                    <td className="lop-cell-number lop-cell-strong lop-col--el">{row.elBalance.toFixed(2)}</td>
                    <td className="lop-cell-number lop-col--el">{row.elPlanned.toFixed(2)}</td>
                    <td className={`lop-cell-number lop-col--sl ${row.employmentType === 'Contractor' && row.slTakenYtd > row.sickAllocated + row.sickRollover ? 'lop-cell-danger' : ''}`} title={row.employmentType === 'Contractor' && row.slTakenYtd > row.sickAllocated + row.sickRollover ? 'Sick leave taken this year exceeds policy entitlement' : undefined}>{row.slTakenYtd.toFixed(2)}</td>
                    <td className="lop-cell-number lop-col--sl">{row.slTakenMtd.toFixed(2)}</td>
                    <td className={`lop-cell-number lop-col--sl ${row.sickExcess > 0 ? 'lop-cell-danger' : ''}`} title={row.sickExcess > 0 ? 'Sick leave taken exceeds available policy balance' : undefined}>{row.slTakenSelected.toFixed(2)}</td>
                    <td className="lop-cell-number lop-col--comp">{row.compTakenYtd.toFixed(2)}</td>
                    <td className="lop-cell-number lop-col--comp">{row.compTakenMtd.toFixed(2)}</td>
                    <td className="lop-cell-number lop-col--comp">{row.compTakenSelected.toFixed(2)}</td>
                    <td className={`lop-cell-number ${row.lossOfPayDays > 0 ? 'lop-cell-danger' : ''}`} title={row.lossOfPayDays > 0 ? 'Mark as LOP' : undefined}>{row.lossOfPayDays.toFixed(2)}</td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => openDetailsModal(row)}>
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailsRow && (
        <div className="modal-overlay" onClick={closeDetailsModal}>
          <div className="modal view-modal lop-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{detailsRow.name} — {startDate} to {endDate}</h3>
              <button className="btn btn-ghost btn-sm" onClick={closeDetailsModal}>✕</button>
            </div>

            <div className="view-tabs">
              <button
                className={`view-tab ${detailsTab === 'leaves' ? 'active' : ''}`}
                onClick={() => setDetailsTab('leaves')}
              >
                Leaves <span className="view-tab-count">{detailsData.leaves.length}</span>
              </button>
              <button
                className={`view-tab ${detailsTab === 'attendance' ? 'active' : ''}`}
                onClick={() => setDetailsTab('attendance')}
              >
                Attendance <span className="view-tab-count">{detailsData.attendance.length}</span>
              </button>
            </div>

            <div className="modal-body">
              {detailsLoading ? (
                <div style={{ padding: '24px 0' }}>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="skeleton" style={{ height: 36, marginBottom: 8, borderRadius: 8 }} />
                  ))}
                </div>
              ) : detailsTab === 'leaves' ? (
                detailsData.leaves.length > 0 ? (
                  <div className="table-wrapper" style={{ border: 'none', maxHeight: 360 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Leave Type</th>
                          <th>Date(s)</th>
                          <th>Days</th>
                          <th>Status</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsData.leaves.map((leave) => (
                          <tr key={leave.id}>
                            <td>{leave.leave_type}</td>
                            <td>
                              {leave.start_date === leave.end_date
                                ? leave.start_date
                                : `${leave.start_date} to ${leave.end_date}`}
                            </td>
                            <td>{leave.days_requested}</td>
                            <td>{leave.status}</td>
                            <td>{leave.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-text">No leave records found for this period.</div>
                  </div>
                )
              ) : detailsData.attendance.length > 0 ? (
                <div className="table-wrapper" style={{ border: 'none', maxHeight: 360 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Check-in</th>
                        <th>Check-out</th>
                        <th>Work Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsData.attendance.map((record) => (
                        <tr key={record.id}>
                          <td>{record.date}</td>
                          <td>{record.status}</td>
                          <td>{record.check_in || '—'}</td>
                          <td>{record.check_out || '—'}</td>
                          <td>{record.work_description || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-text">No attendance records found for this period.</div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleDetailsExportCsv}>
                Export CSV
              </button>
              <button className="btn btn-secondary" onClick={handleDetailsDownloadPdf}>
                Download PDF
              </button>
              <button className="btn btn-primary" onClick={closeDetailsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}